/**
 * Utility functions for fetching BNB price from CoinGecko API
 */

export interface BNBPrice {
  usd: number;
  lastUpdated: number;
}

const COINGECKO_API_URL = 'https://api.coingecko.com/api/v3/simple/price?ids=binancecoin&vs_currencies=usd';
const CACHE_DURATION = 60 * 1000; // 60 seconds
const STORAGE_KEY = 'bnb_price_cache';

// Fallback BNB price if API fails
const FALLBACK_BNB_PRICE = 600; // Approximate BNB price in USD

/**
 * Get cached BNB price from localStorage
 */
function getCachedPrice(): BNBPrice | null {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (!cached) return null;
    
    const data: BNBPrice = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - data.lastUpdated < CACHE_DURATION) {
      return data;
    }
    
    // Cache expired
    localStorage.removeItem(STORAGE_KEY);
    return null;
  } catch (error) {
    console.error('Error reading BNB price cache:', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Cache BNB price to localStorage
 */
function setCachedPrice(price: number): void {
  try {
    const data: BNBPrice = {
      usd: price,
      lastUpdated: Date.now()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error caching BNB price:', error);
  }
}

/**
 * Fetch BNB price from CoinGecko API
 */
export async function fetchBNBPrice(): Promise<number> {
  // First try to get cached price
  const cached = getCachedPrice();
  if (cached) {
    return cached.usd;
  }

  try {
    const response = await fetch(COINGECKO_API_URL, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const price = data.binancecoin?.usd;

    if (typeof price !== 'number' || price <= 0) {
      throw new Error('Invalid price data received');
    }

    // Cache the successful response
    setCachedPrice(price);
    
    return price;
  } catch (error) {
    console.error('Error fetching BNB price:', error);
    
    // Return cached price if available, even if expired
    const cached = getCachedPrice();
    if (cached) {
      console.warn('Using expired cached BNB price due to API error');
      return cached.usd;
    }
    
    // Final fallback
    console.warn(`Using fallback BNB price: $${FALLBACK_BNB_PRICE}`);
    return FALLBACK_BNB_PRICE;
  }
}

/**
 * Convert BNB amount to USD
 */
export function convertBNBToUSD(bnbAmount: number, bnbPrice: number): number {
  return bnbAmount * bnbPrice;
}

/**
 * Format USD amount for display
 */
export function formatUSD(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(2)}K`;
  }
  if (amount >= 100) {
    return `$${amount.toFixed(0)}`;
  }
  if (amount >= 10) {
    return `$${amount.toFixed(1)}`;
  }
  if (amount >= 1) {
    return `$${amount.toFixed(2)}`;
  }
  return `$${amount.toFixed(3)}`;
}